import React, { useEffect, useState, useContext } from 'react'
import { Redirect, withRouter } from 'react-router-dom'
import { useLazyQuery, useQuery } from '@apollo/client'
import PropTypes from 'prop-types'
import styled from '@emotion/styled/macro'
import lodash from 'lodash'

import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'
import { MessageFormat } from '@paypalcorp/worldready'

import getAllProducts from '../../graphql/getAllProducts'
import getStoresByLocation from '../../graphql/getStoresByLocation'
import VoucherSection from './VoucherSection'
import LocationBasedProductSection from './LocationBasedProductSection'
import { handleSessionError } from '../../utils/error-tracking'
import { SearchHero } from '../../components/hero'
import { Loader } from '../../components/loader'

const BusinessesScreenContainer = styled.div`
  padding-bottom: 50px;

  @media only screen and (max-width: 768px) {
    padding: 20px 0;
  }
`

const HeroHeadlineComponent = styled.div`
  font-size: 20px;
  margin: auto auto 34px;
  max-width: 634px;
`

const BusinessProductsSection = styled.div`
  max-width: 852px;
  margin: auto;
`

const ViewMoreDiv = styled.div`
  display: flex;
  align-items: center;
`

const Icon = styled.div`
  padding-left: 4px;

  @media only screen and (max-width: 400px) {
    display: none;
  }
`

const ViewMore = (
  <ViewMoreDiv>
    <Message id="pages/businesses.businesses.local.section.viewMore" />
    <Icon />
  </ViewMoreDiv>
)

const animationImage = 'https://pics.paypal.com/00/c/gifts/us/bgtile.png'

const Businesses = ({ history }) => {
  const worldready = useContext(WorldReadyContext)

  const [gotAllGiftCards, setGotAllGiftCards] = useState(false)
  const [searchLocationName, setSearchLocationName] = useState('')
  const [geolocationLoading, setGeolocationLoading] = useState(false)

  const [browserLocation, setBrowserLocation] = useState({
    latitude: null,
    longitude: null,
  })

  const [
    allProductsInBusinessCategory,
    setAllProductsInBusinessCategory,
  ] = useState([])

  const { loading, data, error } = useQuery(getAllProducts)

  const [getStores, { data: storesData }] = useLazyQuery(getStoresByLocation)

  const MAX_NUMBER_OF_GIFT_CARDS_TO_DISPLAY = 6
  const NO_LOCATION_DEFAULT_LOCATION_TEXT = 'default'

  useEffect(() => {
    window.scrollTo(0, 0)
    setGeolocationLoading(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBrowserLocation({
          latitude: lodash.get(position, 'coords.latitude', 0),
          longitude: lodash.get(position, 'coords.longitude', 0),
        })
        setGeolocationLoading(false)
      },
      (err) => {
        console.error(
          `Failed to get browser location. Err: ${JSON.stringify(err)}`,
        )
        // on error retrieving browser location, set lat/long to 0, 0
        // no products will be returned for this lat/long, thus all_a_z gifts will be shown
        setBrowserLocation({
          latitude: 0,
          longitude: 0,
        })
        setGeolocationLoading(false)
      },
      { timeout: 5000 },
    )
  }, [])

  useEffect(() => {
    if (isNaN(parseFloat(browserLocation.latitude))) return
    if (isNaN(parseFloat(browserLocation.longitude))) return

    getStores({
      variables: {
        locationToSearch: browserLocation,
      },
    })
  }, [browserLocation])

  useEffect(() => {
    if (!data || !data.getAllProducts) return

    const allGiftCards = lodash.get(data, 'getAllProducts.all_a_z', [])
    const stores = lodash.get(storesData, 'getStoresByLocation.stores', [])

    const storeMerchantIDMap = stores.reduce(
      (map, store) => ({
        ...map,
        [store.merchantId]: store,
      }),
      {},
    )

    const allSMBGiftCards = allGiftCards.filter((gc) =>
      gc.categories.includes('Small Business'),
    )

    const filteredGiftCardsWithLocation = allSMBGiftCards.reduce(
      (giftCards, giftCard) => {
        const matchingStore = storeMerchantIDMap[giftCard.merchant_id]
        const giftCardWithAddress = {
          ...giftCard,
          address: lodash.get(matchingStore, 'address'),
        }

        if (matchingStore) return [...giftCards, giftCardWithAddress]
        return giftCards
      },
      [],
    )

    const hasFilteredGiftCards =
      filteredGiftCardsWithLocation && filteredGiftCardsWithLocation.length > 0

    const getNewSearchLocation = () => {
      if (hasFilteredGiftCards) {
        const giftCardWithCityState = filteredGiftCardsWithLocation.find(
          (gc) => gc.address && gc.address.city && gc.address.state,
        )

        if (giftCardWithCityState) {
          return `${giftCardWithCityState.address.city}, ${giftCardWithCityState.address.state}`
        }
      }

      return NO_LOCATION_DEFAULT_LOCATION_TEXT
    }

    setAllProductsInBusinessCategory(
      hasFilteredGiftCards ? filteredGiftCardsWithLocation : allSMBGiftCards,
    )

    setGotAllGiftCards(!hasFilteredGiftCards)
    setSearchLocationName(getNewSearchLocation())
  }, [data, storesData])

  if (error) {
    handleSessionError(error)
    return <Redirect to="/error" />
  }

  const businessSearchRedirectUrl = (queryValue) => {
    return `/business/search/?q=${encodeURIComponent(
      JSON.stringify(queryValue),
    )}`
  }

  const onSearchLocation = (value) => {
    setSearchLocationName(value)
    const updatedValue = typeof value === 'string' ? { zipCode: value } : value
    history.push(businessSearchRedirectUrl(updatedValue))
  }

  const onClickViewMore = () => {
    history.push(businessSearchRedirectUrl(browserLocation))
  }

  const isLoading = loading || geolocationLoading || !searchLocationName

  return (
    <BusinessesScreenContainer>
      <SearchHero
        headline={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.hero.title',
        }).format()}
        headlineSize="sm"
        headlinePadding="80px 56px 24px"
        headlineStyle={{
          maxWidth: '720px',
          margin: 'auto',
          fontSize: '3em',
        }}
        headlineComponent={
          <HeroHeadlineComponent>
            <Message id="pages/businesses.businesses.hero.subtitle" />
          </HeroHeadlineComponent>
        }
        searchLabel={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.hero.search.label',
        }).format()}
        imageStyle={{
          animation: 'bg-scrolling-reverse 13s infinite',
          animationTimingFunction: 'linear',
          backgroundColor: 'white',
          transform: 'scale(1)',
          backgroundRepeat: 'repeat',
        }}
        mediaSrc={{ sm: animationImage, lg: animationImage }}
        onSearchSubmit={onSearchLocation}
        enableSeeYourLocation={true}
        currentLocationLabel={new MessageFormat(worldready, {
          id: 'pages/businesses.businesses.local.currentLocation',
        }).format()}
      />
      <BusinessProductsSection>
        {isLoading && <Loader style={{ paddingTop: '400px' }} />}
        {!isLoading && (
          <LocationBasedProductSection
            title={new MessageFormat(worldready, {
              id: 'pages/businesses.businesses.local.section.title',
            }).format()}
            subtitle={
              (!gotAllGiftCards &&
                new MessageFormat(worldready, {
                  id:
                    'pages/businesses.businesses.local.section.subtitle.primary',
                }).format()) ||
              ''
            }
            buttonContent={
              allProductsInBusinessCategory.length >
              MAX_NUMBER_OF_GIFT_CARDS_TO_DISPLAY
                ? ViewMore
                : null
            }
            locationName={
              searchLocationName === NO_LOCATION_DEFAULT_LOCATION_TEXT
                ? ''
                : searchLocationName
            }
            productList={allProductsInBusinessCategory.slice(
              0,
              MAX_NUMBER_OF_GIFT_CARDS_TO_DISPLAY,
            )}
            displayNameAndAddressCaption={true}
            redirectToSearchRouteOnNull={true}
            history={history}
            onClickButton={onClickViewMore}
          />
        )}
        <VoucherSection
          showFiltered={false}
          storesData={lodash.get(storesData, 'getStoresByLocation.stores', [])}
          locationName={
            searchLocationName === NO_LOCATION_DEFAULT_LOCATION_TEXT
              ? ''
              : searchLocationName
          }
        />
      </BusinessProductsSection>
    </BusinessesScreenContainer>
  )
}

Businesses.propTypes = {
  history: PropTypes.object.isRequired,
}

export default withRouter(Businesses)
